// Central icon registry for the Zupp Admin design system.
//
// One place maps a domain concept or action to exactly one Heroicon
// (svelte-heros-v2). Reference icons by their string key everywhere — via the
// <Icon name="..." /> component — instead of importing icon components ad hoc.
// This keeps every "edit" pencil, every "delete" trash, and every nav glyph
// identical across the app, and makes a global icon swap a one-line change here.
//
// Adding an icon: import it below and add a `key: Component` entry. Unknown keys
// resolve to QuestionMarkCircle so a typo degrades visibly instead of crashing.

import {
  AcademicCap,
  AdjustmentsHorizontal,
  ArrowDownTray,
  ArrowLeft,
  ArrowPath,
  ArrowRightOnRectangle,
  ArrowTopRightOnSquare,
  ArrowTrendingUp,
  ArrowUturnLeft,
  Bars2,
  Bars3,
  Bolt,
  BuildingLibrary,
  CalendarDays,
  ChartBar,
  ChartBarSquare,
  ChatBubbleLeftRight,
  Check,
  ChevronDoubleLeft,
  ChevronDoubleRight,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  CloudArrowUp,
  Cog6Tooth,
  ComputerDesktop,
  CreditCard,
  CurrencyDollar,
  DocumentChartBar,
  DocumentDuplicate,
  ExclamationCircle,
  ExclamationTriangle,
  Eye,
  FaceSmile,
  Film,
  Flag,
  Funnel,
  Gift,
  GiftTop,
  Home,
  InboxArrowDown,
  InformationCircle,
  Link,
  LockClosed,
  MagnifyingGlass,
  Map,
  MapPin,
  Megaphone,
  Moon,
  Newspaper,
  PaperAirplane,
  Pause,
  PencilSquare,
  Photo,
  Play,
  Plus,
  PresentationChartLine,
  QuestionMarkCircle,
  QueueList,
  RectangleStack,
  ShieldCheck,
  Sparkles,
  Squares2x2,
  Star,
  Sun,
  Tag,
  Trash,
  Trophy,
  UserCircle,
  UserGroup,
  Users,
  XMark
} from 'svelte-heros-v2'

export const ICONS = {
  // ---- Navigation / domain concepts -------------------------------------
  dashboard: Squares2x2,
  campaigns: Megaphone,
  adventures: Map,
  feeds: Newspaper,
  boosts: Bolt,
  college_towns: BuildingLibrary,
  colleges: AcademicCap,
  places: MapPin,
  point_sources: GiftTop,
  reward: Gift,
  polls: ChartBarSquare,
  categories: Tag,
  media: Photo,
  reports: DocumentChartBar,
  quests: Trophy,
  quest_submissions: InboxArrowDown,
  media_uploads: CloudArrowUp,
  engagement_dashboard: ArrowTrendingUp,
  quest_dashboard: ChartBar,
  moments: Film,
  social_dashboard: PresentationChartLine,
  moderation: ShieldCheck,
  user_icons: FaceSmile,
  feed_ranking: AdjustmentsHorizontal,
  user_reports: Flag,
  users: Users,
  admins: UserGroup,

  // ---- Actions ----------------------------------------------------------
  add: Plus,
  edit: PencilSquare,
  duplicate: DocumentDuplicate,
  delete: Trash,
  undo: ArrowUturnLeft,
  download: ArrowDownTray,
  view: Eye,
  resend: PaperAirplane,
  search: MagnifyingGlass,
  filter: Funnel,
  refresh: ArrowPath,
  pause: Pause,
  play: Play,
  waiting: Clock,
  person: UserCircle,
  money: CurrencyDollar,
  credit: CreditCard,
  calendar: CalendarDays,
  external: ArrowTopRightOnSquare,
  link: Link,
  thumbnail: Photo,
  sign_out: ArrowRightOnRectangle,
  settings: Cog6Tooth,

  // ---- UI affordances ---------------------------------------------------
  menu: Bars3,
  drag_handle: Bars2,
  back: ArrowLeft,
  close: XMark,
  lock: LockClosed,
  check: Check,
  chevron_down: ChevronDown,
  chevron_up: ChevronUp,
  chevron_left: ChevronLeft,
  chevron_right: ChevronRight,
  sidebar_collapse: ChevronDoubleLeft,
  sidebar_expand: ChevronDoubleRight,
  theme_light: Sun,
  theme_dark: Moon,
  theme_auto: ComputerDesktop,
  home: Home,
  list: QueueList,
  stack: RectangleStack,
  sparkles: Sparkles,
  star: Star,
  chat: ChatBubbleLeftRight,

  // ---- Status / feedback ------------------------------------------------
  info: InformationCircle,
  success: Check,
  warning: ExclamationTriangle,
  error: ExclamationCircle,

  unknown: QuestionMarkCircle
}

export function iconFor(name) {
  return ICONS[name] ?? ICONS.unknown
}

export const ICON_NAMES = Object.keys(ICONS)
